import network from '@common/network/network';
import {responseToBase64String} from '@common/utils/utils';

function getFamilyTreeContentShareByEmail(contentShares, {id, email}) {
  return (contentShares || []).find(share => {
    const familyTreeId =
      share.family_tree_id || (share.family_tree && (share.family_tree.id || share.family_tree.object_id));
    return `${familyTreeId}` === `${id}` && share.shared_with_email === email;
  });
}

function rejectMissingFamilyTreeContentShare({id, email}) {
  return Promise.reject(new Error(`Missing content share for family tree ${id} and email ${email}.`));
}

export default {
  fetchFamilyTreeMapAction(context, {familyTreeId, colorCode, startPerson, mapHash}) {
    context.commit('setFamilyTreeMapImageLoadingState', true);
    return network.familyTree
      .getMap({
        id: familyTreeId,
        color: colorCode,
        start_person_id: startPerson,
        map_hash: mapHash,
        scale: context.getters.familyTreeMapImageScaleState,
      })
      .then(response => {
        context.commit('setFamilyTreeMapImageState', responseToBase64String(response));
      })
      .catch(e => {
        console.error(e);
        context.commit('setFamilyTreeMapImageState', null);
      })
      .finally(() => {
        context.commit('setFamilyTreeMapImageLoadingState', false);
      });
  },
  fetchFamilyTreeMapDiffAction(context, params = {}) {
    context.commit('setFamilyTreeDrawLoadingState', true);

    const writeAllowed = context.getters.isFamilyTreeWriteAllowedState;
    params = {...params, id: params.id || 'my', with_ghosts: writeAllowed ? 1 : 0};
    return new Promise((resolve, reject) => {
      network.familyTree
        .getDrawnDiff(params)
        .then(response => {
          if (!response) {
            // if http status is 204 do full load
            context.dispatch('fetchFamilyTreeAction', params);
            resolve();
          }

          const startPersonId = params.start_person_id;
          context.commit('setFamilyTreeBloodRelativesIdsState', response.blood_relatives_ids);
          context.commit('mutateFamilyTreePersonsByDiff', {
            new_persons: response.new_persons,
            remove_persons: response.remove_persons,
            data_update: response.data_update,
          });
          context.commit('mutateFamilyTreeLinesByDiff', {
            new_lines: response.new_lines,
            remove_lines: response.remove_lines,
          });
          context.commit('setFamilyTreeDrawnWidthState', response.tree_width);
          context.commit('setFamilyTreeDrawnHeightState', response.tree_height);
          context.commit('setFamilyTreeDrawnMarginXState', response.tree_margin_x);
          context.commit('setIsExpandControlChangedState', response.has_state);

          context.commit('setFamilyTreeMapImageHashState', response.map_hash);

          const startPerson = context.getters.familyTreePersonsByIdState[startPersonId];
          context.commit('setFamilyTreeStartPersonYState', startPerson.draws ? startPerson.draws[0].position.y : null);

          // const drawnPersons = context.getters.familyTreePersonsDrawnState;

          context.dispatch('fetchFamilyTreeMapAction', {
            familyTreeId: params.id,
            colorCode: response.preferences.color_code_gender ? '1' : '0',
            startPerson: startPersonId,
            mapHash: response.map_hash,
          });
          resolve();
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          context.commit('setFamilyTreeDrawLoadingState', false);
          context.commit('setFamilyTreeRefreshRequestState', true);
        });
    });
  },
  fetchFamilyTreeAction(context, params = {}) {
    console.log('full load');
    context.commit('setFamilyTreeDrawLoadingState', true);
    params = {...params, id: params.id || 'my'};
    return new Promise((resolve, reject) => {
      network.familyTree
        .getDrawn(params)
        .then(response => {
          const startPerson = response.persons.find(p => p.is_start_person);
          const startPersonId = startPerson.object_id;
          if (`${context.getters.familyTreeDetailsIdState}` !== `${response.id}`) {
            context.commit('clearFamilyTreeDetachedSubtreesState');
          }
          context.commit('setFamilyTreeNameState', response.name);
          context.commit('setFamilyTreeHomePerson', response.home_person);
          context.commit('setFamilyTreePersonsDrawnState', response.persons);
          context.commit('setFamilyTreeLinesDrawnState', response.lines);
          context.commit('setFamilyTreeDetailsIdState', response.id);
          context.commit('setActiveFamilyTreeIdState', response.id);
          context.commit('setFamilyTreeStartPersonIdState', startPersonId);
          context.commit('setFamilyTreeStartPersonYState', startPerson.draws ? startPerson.draws[0].position.y : null);
          context.commit('setFamilyTreeIsWriteAllowedState', response.is_write_allowed);
          context.commit('setFamilyTreeIsUserOwnerState', response.is_user_owner);
          context.commit('setFamilyTreeIsPublicState', response.is_public);
          context.commit('setFamilyTreeDrawnWidthState', response.tree_width);
          context.commit('setFamilyTreeDrawnHeightState', response.tree_height);
          context.commit('setFamilyTreeDrawnMarginXState', response.tree_margin_x);
          context.commit('setFamilyTreePreferencesState', response.preferences);
          context.commit('setFamilyTreeBloodRelativesIdsState', response.blood_relatives_ids);
          context.commit('setFamilyTreeHasDetachedSubtreesState', Boolean(response.has_detached_subtrees));
          context.commit('setFamilyTreeMapImageHashState', response.map_hash);
          context.commit('setOnceShowFindOwnedTreeTooltipState', !response.is_user_owner);
          context.commit('setIsExpandControlChangedState', response.has_state);

          const breadcrumbs = context.getters.treeBreadcrumbsState;
          if (breadcrumbs && breadcrumbs.treeId === response.id) {
            context.commit('syncTreeBreadcrumbsPersonsState', {persons: response.persons});
            context.commit('handleTreeBreadcrumbsState', {person: startPerson});
          } else {
            context.commit('initTreeBreadcrumbsState', {treeId: response.id, homePerson: response.home_person});
          }
          context.dispatch('fetchFamilyTreeMapAction', {
            familyTreeId: params.id,
            colorCode: response.preferences.color_code_gender ? '1' : '0',
            startPerson: startPersonId,
            mapHash: response.map_hash,
          });
          resolve();
        })
        .catch(async error => {
          console.log('error!', error);
          await context.dispatch('cleanupTreeStateAction');
          if (parseInt(context.getters.activeFamilyTreeIdState) === parseInt(params.id)) {
            context.commit('clearActiveFamilyTreeIdState');
          }
          reject(error);
        })
        .finally(() => {
          context.commit('setFamilyTreeDrawLoadingState', false);
          // context.commit('setFamilyTreeRefreshRequestState', true);
        });
    });
  },
  cleanupTreeStateAction(context) {
    context.commit('setFamilyTreeNameState', null);
    context.commit('setFamilyTreeHomePerson', null);
    context.commit('setFamilyTreePersonsDrawnState', []);
    context.commit('setFamilyTreeLinesDrawnState', []);
    context.commit('setFamilyTreeDetailsIdState', null);
    context.commit('setFamilyTreeStartPersonIdState', null);
    context.commit('setFamilyTreeStartPersonYState', null);
    context.commit('setFamilyTreeIsWriteAllowedState', false);
    context.commit('setFamilyTreeIsUserOwnerState', false);
    context.commit('setFamilyTreeIsPublicState', false);
    context.commit('setFamilyTreeDrawnWidthState', null);
    context.commit('setFamilyTreeDrawnHeightState', null);
    context.commit('setFamilyTreeDrawnMarginXState', 0);
    context.commit('setFamilyTreePreferencesState', {});
    context.commit('setFamilyTreeHasDetachedSubtreesState', false);
    context.commit('clearFamilyTreeDetachedSubtreesState');
    context.commit('setIsExpandControlChangedState', false);
    context.commit('clearTreeBreadcrumbsState');
  },
  fetchFamilyTreeSubtreesAction(context, familyTreeId) {
    context.commit('setFamilyTreeDetachedSubtreesLoadingState', {familyTreeId, loading: true});
    return network.familyTree
      .getSubtrees({id: familyTreeId})
      .then(response => {
        context.commit('setFamilyTreeDetachedSubtreesState', {
          familyTreeId,
          subtrees: response.detached_subtrees || [],
        });
        return response;
      })
      .finally(() => {
        context.commit('setFamilyTreeDetachedSubtreesLoadingState', {familyTreeId, loading: false});
      });
  },
  fetchFamilyTreeListAction(context, {fields} = {}) {
    context.commit('setFamilyTreeListLoadingState', true);
    return new Promise((resolve, reject) => {
      network.familyTree
        .list({fields})
        .then(response => {
          context.commit('setFamilyTreeListState', response);
        })
        .catch(() => {})
        .finally(() => {
          context.commit('setFamilyTreeListLoadingState', false);
        });
    });
  },
  fetchFamilyTreeDetailsAction(context, id) {
    context.commit('setFamilyTreeDetailsLoadingState', true);
    return new Promise((resolve, reject) => {
      network.familyTree
        .details({id})
        .then(response => {
          context.commit('setFamilyTreeDetailsState', response);
          resolve(response);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setFamilyTreeDetailsLoadingState', false);
        });
    });
  },
  updateFamilyTreeAction(context, {id, is_public, name, home_person_id}) {
    context.commit('setFamilyTreeUpdateLoadingByIdState', {id, loading: true});
    return new Promise((resolve, reject) => {
      network.familyTree
        .update({id, is_public, name, home_person_id})
        .then(response => {
          context.commit('mutateFamilyTreeNameInListState', {id, name: response.name});
          if (context.getters.familyTreeDetailsIdState === id) {
            context.commit('setFamilyTreeNameState', response.name);
            context.commit('setFamilyTreeHomePerson', response.home_person);
          }
          resolve(response);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setFamilyTreeUpdateLoadingByIdState', {id, loading: false});
        });
    });
  },
  searchPersonsInFamilyTreeAction(context, {familyTreeId, q, contextTreeId}) {
    const requestId = (context.state.personsSearchListRequestId || 0) + 1;
    context.commit('setFamilyTreePersonsSearchListRequestIdState', requestId);
    context.commit('setFamilyTreePersonsSearchListLoadingState', true);
    const search = familyTreeId
      ? network.familyTree.searchPersons({id: familyTreeId, query: q})
      : network.familyTree.searchPersonsInAllTrees({query: q, context_tree_id: contextTreeId});

    return search
      .then(response => {
        if (context.state.personsSearchListRequestId !== requestId) {
          throw new Error('Person search request was superseded.');
        }
        context.commit('setFamilyTreePersonsSearchListState', response.objects);
        context.commit('setFamilyTreePersonsSearchListMetaState', response.meta);
        return response;
      })
      .finally(() => {
        if (context.state.personsSearchListRequestId === requestId) {
          context.commit('setFamilyTreePersonsSearchListLoadingState', false);
        }
      });
  },
  searchMostTaggedLocationsInFamilyTreeAction(context, {familyTreeId, q}) {
    context.commit('setFamilyTreeMostTaggedLocationsLoadingState', true);
    return new Promise((resolve, reject) => {
      network.familyTree
        .getMostTaggedLocations({id: familyTreeId, q})
        .then(response => {
          context.commit('setFamilyTreeMostTaggedLocationsState', response);
          resolve(response);
        })
        .finally(() => {
          context.commit('setFamilyTreeMostTaggedLocationsLoadingState', false);
        });
    });
  },
  fetchFamilyTreeSurnamesListAction(context, familyTreeId) {
    context.commit('setFamilyTreeSurnamesLoadingState', true);
    return new Promise((resolve, reject) => {
      network.familyTree
        .getChineseSurnames({id: familyTreeId})
        .then(response => {
          context.commit('setFamilyTreeSurnamesState', response.objects || response);
          resolve(response);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setFamilyTreeSurnamesLoadingState', false);
        });
    });
  },
  fetchFamilyTreeClanMetaAction(context, {familyTreeId, surname}) {
    return new Promise((resolve, reject) => {
      network.familyTree
        .geTreeClanMeta({id: familyTreeId, q: surname})
        .then(response => {
          context.commit('setTreeClanMetaState', response);
          resolve(response);
        })
        .catch(reject);
    });
  },
  createFamilyTreeAction(context, {start_person, context_user_id, tree_name}) {
    return new Promise((resolve, reject) => {
      network.familyTree.create({start_person, context_user_id, tree_name}).then(resolve).catch(reject);
    });
  },
  moveFamilyTreeToAccountAction(context, {id, user_id}) {
    return new Promise((resolve, reject) => {
      network.familyTree.moveToAccount({id, user_id}).then(resolve).catch(reject);
    });
  },
  deleteFamilyTreeAction(context, {id}) {
    return new Promise((resolve, reject) => {
      network.familyTree.delete({id}).then(resolve).catch(reject);
    });
  },
  setFamilyTreeDefaultExpandControlAction(context, {id, start_person_id}) {
    return new Promise((resolve, reject) => {
      network.familyTree.setDefaultExpandControl({id, start_person_id}).then(resolve).catch(reject);
    });
  },
  familyTreeAddMembersAction(context, {id, emails}) {
    context.commit('setFamilyTreeAddMembersLoadingState', true);
    const sharePayloads = emails.map(email => ({
      shared_with_email: email,
      family_tree_id: id,
      share_everything: false,
      is_editor: false,
    }));
    return network.fws.createContentShare(sharePayloads).finally(() => {
      context.commit('setFamilyTreeAddMembersLoadingState', false);
    });
  },
  familyTreeEditMemberAction(context, {id, email, is_editor}) {
    context.commit('setFamilyTreeEditMemberLoadingState', {email, loading: true});
    const updateContentShare = () => {
      const share = getFamilyTreeContentShareByEmail(context.getters.familytreeContentSharesState, {id, email});
      if (!share || !share.id) {
        return rejectMissingFamilyTreeContentShare({id, email});
      }
      return network.fws.updateContentShare({id: share.id, is_editor});
    };
    return updateContentShare()
      .catch(error => {
        if (error && !error.message?.startsWith('Missing content share')) {
          return Promise.reject(error);
        }
        return context.dispatch('fetchFamilytreeContentSharesAction', {id}).then(updateContentShare);
      })
      .finally(() => {
        context.commit('setFamilyTreeEditMemberLoadingState', {email, loading: false});
      });
  },
  familyTreeDeleteMemberAction(context, {id, email}) {
    context.commit('setFamilyTreeDeleteMemberLoadingState', {email, loading: true});
    const deleteContentShare = () => {
      const share = getFamilyTreeContentShareByEmail(context.getters.familytreeContentSharesState, {id, email});
      if (!share || !share.id) {
        return rejectMissingFamilyTreeContentShare({id, email});
      }
      return network.fws.deleteContentShare({id: share.id});
    };
    return deleteContentShare()
      .catch(error => {
        if (error && !error.message?.startsWith('Missing content share')) {
          return Promise.reject(error);
        }
        return context.dispatch('fetchFamilytreeContentSharesAction', {id}).then(deleteContentShare);
      })
      .finally(() => {
        context.commit('setFamilyTreeDeleteMemberLoadingState', {email, loading: false});
      });
  },
  fetchFamilytreeContentSharesAction(context, {id}) {
    context.commit('setFamilytreeContentSharesLoadingState', true);
    return new Promise((resolve, reject) => {
      network.fws
        .getContentShares()
        .then(response => {
          const contentShares = response.content_shares || response || [];
          context.commit('setFamilytreeContentSharesState', contentShares);
          resolve(contentShares);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setFamilytreeContentSharesLoadingState', false);
        });
    });
  },
  acceptFamilytreeContentShareSuggestionAction(
    context,
    {shared_with_email, family_tree_id, share_everything, is_editor}
  ) {
    context.commit('setFamilytreeContentShareAcceptLoadingState', {email: shared_with_email, loading: true});
    return new Promise((resolve, reject) => {
      network.fws
        .createContentShare({
          shared_with_email,
          family_tree_id,
          share_everything,
          is_editor,
        })
        .then(response => {
          context
            .dispatch('fetchFamilytreeContentSharesAction', {id: family_tree_id})
            .then(() => {
              resolve(response);
            })
            .catch(reject);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setFamilytreeContentShareAcceptLoadingState', {email: shared_with_email, loading: false});
        });
    });
  },

  fetchAllTreesPhotosAction(context, {offset, limit, tree_id, context_tree_id, search}) {
    return new Promise((resolve, reject) => {
      network.familyTree
        .getAllPhotos({offset, limit, tree_id, context_tree_id, search})
        .then(response => {
          context.commit('addToAllTreesPhotosState', response.objects);
          resolve(response);
        })
        .catch(reject);
    });
  },
  fetchAllTreesAssetsAction(context, {offset, limit, tree_id, context_tree_id, search}) {
    return new Promise((resolve, reject) => {
      network.familyTree
        .getAllAssets({offset, limit, tree_id, context_tree_id, search})
        .then(response => {
          resolve(response);
        })
        .catch(reject);
    });
  },

  fetchTreOwnershipDataAction(context, {tree_id}) {
    context.commit('setTreeOwnershipDataLoadingState', false);
    return new Promise((resolve, reject) => {
      network.familyTree
        .getOwnershipData({tree_id})
        .then(response => {
          const values = response.objects;
          context.commit('setTreeOwnershipDataState', values && values.length ? values[0] : null);
          resolve(response);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setTreeOwnershipDataLoadingState', false);
        });
    });
  },
  searchTreOwnershipDataAction(context, {user_email}) {
    context.commit('setTreeOwnershipDataSearchLoadingState', false);
    return new Promise((resolve, reject) => {
      network.familyTree
        .getOwnershipData({user_email})
        .then(response => {
          context.commit('setTreeOwnershipDataSearchState', response.objects);
          resolve(response);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setTreeOwnershipDataSearchLoadingState', false);
        });
    });
  },
};
